/**
 * Benutzer und Rollen -- nur fuer Administratoren (KONZEPT.md §4).
 *
 * Zwei Rollen: Administrator und Benutzer. Benutzer werden nie geloescht,
 * nur stillgelegt -- ihre Buchungen sollen zuordenbar bleiben. Was sich nicht
 * rueckgaengig machen laesst, ohne an die Datenbank zu gehen, verweigert der
 * Server: sich selbst die Administratorrolle nehmen und den letzten
 * Administrator abschaffen.
 *
 * Nur mit Netz (ADR 0006).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { geheimnisMangel, type Benutzer, type Rolle } from "@thw/contracts";
import { anfrage, sitzungLesen } from "../../api/klient.js";
import { useOnline } from "../../api/online.js";
import { abgleichen } from "../../lokal/abgleich.js";
import {
  Eingabe,
  Karte,
  Knopf,
  Ladeanzeige,
  NurMitNetz,
  Rueckmeldung,
  Wahl,
} from "../../ui/bausteine.jsx";
import { KENNWORT_REGELN, ROLLEN_TEXT } from "../Konto.jsx";

const ROLLEN: readonly { wert: Rolle; text: string }[] = [
  { wert: "benutzer", text: "Benutzer" },
  { wert: "administrator", text: "Administrator" },
];

const SCHLUESSEL = ["benutzer"] as const;

type Meldung = { art: "gut" | "warnung"; text: string } | null;

export function BenutzerVerwaltung() {
  const online = useOnline();
  const [meldung, setMeldung] = useState<Meldung>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const ich = sitzungLesen()?.benutzer ?? null;

  const { data, isPending } = useQuery({
    queryKey: SCHLUESSEL,
    queryFn: () => anfrage<{ benutzer: Benutzer[] }>("GET", "/api/core/benutzer"),
  });

  if (ich?.rolle !== "administrator") {
    return <Rueckmeldung art="warnung" kopf="Nur für Administratoren" />;
  }
  if (isPending) return <Ladeanzeige was="Die Benutzerliste" />;

  const benutzer = data?.benutzer ?? [];
  const aktive = benutzer.filter((b) => b.aktiv);
  const stillgelegte = benutzer.filter((b) => !b.aktiv);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Benutzer und Rollen</h1>
      {!online && <NurMitNetz />}
      {meldung !== null && <Rueckmeldung art={meldung.art} kopf={meldung.text} />}

      <NeuerBenutzer online={online} melden={setMeldung} />

      <Karte titel={`Aktiv (${String(aktive.length)})`}>
        <ul className="flex flex-col gap-2">
          {aktive.map((b) => (
            <BenutzerZeile
              key={b.id}
              benutzer={b}
              istIch={b.id === ich.id}
              offen={offen === b.id}
              umschalten={() => {
                setOffen(offen === b.id ? null : b.id);
              }}
              online={online}
              melden={setMeldung}
            />
          ))}
        </ul>
      </Karte>

      {stillgelegte.length > 0 && (
        <Karte titel={`Stillgelegt (${String(stillgelegte.length)})`}>
          <ul className="flex flex-col gap-2">
            {stillgelegte.map((b) => (
              <BenutzerZeile
                key={b.id}
                benutzer={b}
                istIch={false}
                offen={offen === b.id}
                umschalten={() => {
                  setOffen(offen === b.id ? null : b.id);
                }}
                online={online}
                melden={setMeldung}
              />
            ))}
          </ul>
        </Karte>
      )}
    </div>
  );
}

function useBenutzerAendern(melden: (m: Meldung) => void) {
  const zwischenspeicher = useQueryClient();
  return useMutation({
    mutationFn: ({ id, aenderung }: { id: string; aenderung: Record<string, unknown> }) =>
      anfrage<Benutzer>("PATCH", `/api/core/benutzer/${id}`, aenderung),
    onSuccess: (b) => {
      melden({ art: "gut", text: `✓ ${b.anzeigename} gespeichert` });
      void zwischenspeicher.invalidateQueries({ queryKey: SCHLUESSEL });
      void abgleichen({ stammdaten: true });
    },
    onError: (f) => {
      melden({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });
}

function BenutzerZeile({
  benutzer,
  istIch,
  offen,
  umschalten,
  online,
  melden,
}: {
  benutzer: Benutzer;
  istIch: boolean;
  offen: boolean;
  umschalten: () => void;
  online: boolean;
  melden: (m: Meldung) => void;
}) {
  const aendern = useBenutzerAendern(melden);
  const [kennwort, setKennwort] = useState("");
  const kennwortFehler = kennwort === "" ? null : geheimnisMangel(kennwort, benutzer.benutzername);

  return (
    <li className="rounded-xl bg-neutral-100 dark:bg-neutral-700">
      <button
        type="button"
        className="flex min-h-14 w-full items-center justify-between gap-3 px-4 py-2 text-left"
        aria-expanded={offen}
        onClick={umschalten}
      >
        <span className="flex flex-col">
          <span className="font-semibold">
            {benutzer.anzeigename}
            {istIch && <span className="ml-2 text-sm font-normal text-neutral-500">(ich)</span>}
          </span>
          <span className="text-sm text-neutral-600 dark:text-neutral-300">
            {benutzer.benutzername}
          </span>
        </span>
        <span
          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            benutzer.rolle === "administrator"
              ? "bg-blue-700 text-white"
              : "bg-neutral-300 text-neutral-800 dark:bg-neutral-600 dark:text-neutral-100"
          }`}
        >
          {ROLLEN_TEXT[benutzer.rolle]}
        </span>
      </button>

      {offen && (
        <div className="flex flex-col gap-3 border-t border-neutral-300 p-4 dark:border-neutral-600">
          {benutzer.aktiv && (
            <Wahl
              beschriftung="Rolle"
              wert={benutzer.rolle}
              moeglichkeiten={ROLLEN}
              aendern={(rolle) => {
                if (rolle !== benutzer.rolle && online) {
                  aendern.mutate({ id: benutzer.id, aenderung: { rolle } });
                }
              }}
            />
          )}

          {benutzer.aktiv && (
            <>
              <Eingabe
                beschriftung="Neues Kennwort oder PIN setzen"
                art="password"
                wert={kennwort}
                aendern={setKennwort}
                hinweis={`Wenn jemand sein Kennwort vergessen hat. ${KENNWORT_REGELN}`}
                autoComplete="new-password"
              />
              {kennwortFehler !== null && (
                <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                  {kennwortFehler}
                </p>
              )}
              <Knopf
                disabled={!online || kennwort === "" || kennwortFehler !== null}
                onClick={() => {
                  aendern.mutate(
                    { id: benutzer.id, aenderung: { geheimnis: kennwort } },
                    {
                      onSuccess: () => {
                        setKennwort("");
                      },
                    },
                  );
                }}
              >
                Kennwort setzen
              </Knopf>
            </>
          )}

          {!istIch && (
            <Knopf
              art={benutzer.aktiv ? "still" : "neben"}
              disabled={!online}
              onClick={() => {
                aendern.mutate({ id: benutzer.id, aenderung: { aktiv: !benutzer.aktiv } });
              }}
            >
              {benutzer.aktiv ? "Stilllegen" : "Wieder aktivieren"}
            </Knopf>
          )}
          {benutzer.aktiv && !istIch && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              Stillgelegte Benutzer können sich nicht mehr anmelden. Ihre Buchungen bleiben mit
              ihrem Namen erhalten.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

function NeuerBenutzer({ online, melden }: { online: boolean; melden: (m: Meldung) => void }) {
  const zwischenspeicher = useQueryClient();
  const [offen, setOffen] = useState(false);
  const [anzeigename, setAnzeigename] = useState("");
  const [benutzername, setBenutzername] = useState("");
  const [rolle, setRolle] = useState<Rolle>("benutzer");
  const [kennwort, setKennwort] = useState("");

  const anlegen = useMutation({
    mutationFn: () =>
      anfrage<Benutzer>("POST", "/api/core/benutzer", {
        anzeigename: anzeigename.trim(),
        benutzername: benutzername.trim(),
        rolle,
        geheimnis: kennwort,
      }),
    onSuccess: (b) => {
      melden({ art: "gut", text: `✓ ${b.anzeigename} angelegt` });
      setAnzeigename("");
      setBenutzername("");
      setRolle("benutzer");
      setKennwort("");
      setOffen(false);
      void zwischenspeicher.invalidateQueries({ queryKey: SCHLUESSEL });
      void abgleichen({ stammdaten: true });
    },
    onError: (f) => {
      melden({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });

  // Der Benutzername folgt dem Anzeigenamen, bis jemand ihn selbst aendert.
  const [nameVonHand, setNameVonHand] = useState(false);
  const vorschlag = (name: string): string =>
    name
      .toLowerCase()
      .replaceAll("ä", "ae")
      .replaceAll("ö", "oe")
      .replaceAll("ü", "ue")
      .replaceAll("ß", "ss")
      .replace(/\s+/g, ".")
      .replace(/[^a-z0-9._-]/g, "");

  const kennwortFehler = kennwort === "" ? null : geheimnisMangel(kennwort, benutzername);
  const gueltig =
    anzeigename.trim() !== "" &&
    /^[a-z0-9._-]{2,60}$/.test(benutzername.trim()) &&
    kennwort !== "" &&
    kennwortFehler === null;

  if (!offen) {
    return (
      <Knopf
        art="haupt"
        disabled={!online}
        onClick={() => {
          setOffen(true);
        }}
      >
        Neuen Benutzer anlegen
      </Knopf>
    );
  }

  return (
    <Karte titel="Neuer Benutzer">
      <Eingabe
        beschriftung="Name"
        wert={anzeigename}
        aendern={(neu) => {
          setAnzeigename(neu);
          if (!nameVonHand) setBenutzername(vorschlag(neu));
        }}
      />
      <Eingabe
        beschriftung="Benutzername zum Anmelden"
        wert={benutzername}
        aendern={(neu) => {
          setNameVonHand(true);
          setBenutzername(neu);
        }}
        hinweis="Kleinbuchstaben, Ziffern, Punkt, Strich."
      />
      <Wahl beschriftung="Rolle" wert={rolle} moeglichkeiten={ROLLEN} aendern={setRolle} />
      <Eingabe
        beschriftung="Erstes Kennwort oder PIN"
        art="password"
        wert={kennwort}
        aendern={setKennwort}
        hinweis={`${KENNWORT_REGELN} Die Person kann es danach unter „Mein Konto“ selbst ändern.`}
        autoComplete="new-password"
      />
      {kennwortFehler !== null && (
        <p className="text-sm font-semibold text-red-700 dark:text-red-300">{kennwortFehler}</p>
      )}
      <div className="flex gap-2">
        <Knopf
          art="haupt"
          disabled={!online || !gueltig || anlegen.isPending}
          onClick={() => {
            anlegen.mutate();
          }}
        >
          Anlegen
        </Knopf>
        <Knopf
          art="still"
          onClick={() => {
            setOffen(false);
          }}
        >
          Abbrechen
        </Knopf>
      </div>
    </Karte>
  );
}
